/**
 * Public FAQs — common questions for new users (no sign-in required).
 */
import React from 'react';
import { Link } from 'react-router-dom';
import { useUser } from '@clerk/clerk-react';
import PageHeader from '../components/PageHeader';
import './AccessibilityPage.css';

const FaqPage = () => {
  const { isSignedIn, isLoaded } = useUser();
  const onAppHome = isLoaded && isSignedIn;
  return (
    <div className="entry-page accessibility-page-stack">
      <PageHeader
        title="FAQs"
        backTo={onAppHome ? '/' : '/sign-in'}
        backLabel={onAppHome ? '← App home' : '← Sign in'}
      >
        <p className="accessibility-page-intro">
          This page is <strong>public</strong>—no sign-in required. For the accessibility statement, open{' '}
          <Link to="/accessibility">Accessibility</Link>; for accepting an invitation, open{' '}
          <Link to="/how-to">Invitation &amp; sign up</Link>.
        </p>
      </PageHeader>

      <section className="accessibility-page-card accessibility-faq" aria-labelledby="faq-heading">
        <h2 id="faq-heading">Frequently Asked Questions</h2>
        <p>
          Quick answers if you&apos;re new to the portal or wondering what it&apos;s for. Open each question to read more.
        </p>

        <details>
          <summary>Can I read these pages without signing in?</summary>
          <p className="accessibility-faq-answer">
            <strong>Yes.</strong> <Link to="/faqs">FAQs</Link>,{' '}
            <Link to="/accessibility">Accessibility Statement</Link>, and{' '}
            <Link to="/how-to">Invitation &amp; sign up</Link> are public. Use <strong>Sign in</strong> in the header when
            you&apos;re ready to use the rest of the portal.
          </p>
        </details>

        <details>
          <summary>What is Ministry Data Portal?</summary>
          <p className="accessibility-faq-answer">
            It&apos;s a secure web app for our ministry team to submit Friday-night attendance and giving numbers, review and
            finalize reports, track Step Study classes, manage inventory, and related tasks—so data stays organized in one
            place instead of scattered spreadsheets or messages.
          </p>
        </details>

        <details>
          <summary>Who is this site for?</summary>
          <p className="accessibility-faq-answer">
            Team members who help with data entry (for example group leads submitting numbers), ministry leads who review
            and approve reports, and others granted access for Step Study, inventory, or other modules. It&apos;s not a
            public website for guests; you sign in with an account your administrator sets up.
          </p>
        </details>

        <details>
          <summary>How do I sign in? Do I need an invitation?</summary>
          <p className="accessibility-faq-answer">
            You sign in with the same method your organization configured (for example email or Google). If you can&apos;t
            sign in or you&apos;ve never been given access, contact your ministry or portal administrator—they assign which
            parts of the app you can use. See <Link to="/how-to">Invitation &amp; sign up</Link> if you were emailed an
            invitation.
          </p>
        </details>

        <details>
          <summary>I signed in but I don&apos;t see some menus or pages. Why?</summary>
          <p className="accessibility-faq-answer">
            Your account only shows what you&apos;re allowed to use. Someone with &quot;Add Group Data&quot; might not see
            Ministry Lead Review, and vice versa. If you need access to a specific area, ask your administrator to update
            your permissions.
          </p>
        </details>

        <details>
          <summary>What is &quot;Add Group Data&quot;?</summary>
          <p className="accessibility-faq-answer">
            It&apos;s how team members submit numbers for a specific Friday and section (for example large group, children,
            donations). You pick the date and section, enter counts, and submit—usually one section per submission. A
            ministry lead later combines sections into the final report. You may also see a floating &quot;Add Group
            Data&quot; button on many screens.
          </p>
        </details>

        <details>
          <summary>What does the Ministry Lead do in the app?</summary>
          <p className="accessibility-faq-answer">
            The Ministry Lead (or someone with review permission) picks a date, checks what the team submitted, can edit
            numbers if needed, adds notes, and submits the <strong>final</strong> report. That step finalizes the data for
            that night and triggers the usual summary process (such as email), depending on how your organization set things
            up.
          </p>
        </details>

        <details>
          <summary>What is Step Study Tracking?</summary>
          <p className="accessibility-faq-answer">
            A tool to track Step Study classes over time: active and graduated cohorts, weekly entries, optional rosters and
            attendance, and trends. Leaders can choose to track by <strong>roster</strong> (names and attendance) or{' '}
            <strong>manual weekly numbers</strong> only—see the instructions on the Step Study pages for details.
          </p>
        </details>

        <details>
          <summary>Can I use this on my phone or tablet?</summary>
          <p className="accessibility-faq-answer">
            Yes. The portal is built to work in the browser on phones, tablets, and desktops. Use a current version of
            Chrome, Safari, Firefox, or Edge for the best experience.
          </p>
        </details>

        <details>
          <summary>Is my data private? Who can see it?</summary>
          <p className="accessibility-faq-answer">
            Data is stored for ministry operations and is only available to authenticated users with the right permissions.
            Don&apos;t share your login. For specific privacy or retention questions, ask your organization&apos;s leadership
            or administrator.
          </p>
        </details>

        <details>
          <summary>Where can I learn how to use a specific feature?</summary>
          <p className="accessibility-faq-answer">
            Open <Link to="/help-videos">Help Videos</Link> for short explainers by topic (starting with Add Group Data).
            Many pages also include expandable <strong>Instructions</strong> or <strong>How this page works</strong> sections at
            the top. You can also open the{' '}
            <Link to="/how-to">Invitation &amp; sign up</Link> page for getting started. For account or permission issues,
            contact your administrator.
          </p>
        </details>

        <details>
          <summary>How do I get help or report a problem?</summary>
          <p className="accessibility-faq-answer">
            For accessibility barriers, open the{' '}
            <Link to="/accessibility">Accessibility Statement</Link> and use the contact email there. For general app issues,
            data corrections, or access requests, reach out to your ministry lead or portal administrator.
          </p>
        </details>
      </section>
    </div>
  );
};

export default FaqPage;
